import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api, downloadBlob } from '../api/client'
import type { SbomApplication, SbomComponent, SbomView, SbomVulnerability } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'
import { RuntimeServizio } from './Runtime'
import { LinkKev } from './Kev'

/**
 * CENSO SBOM in the console: the CI section (its applications and their
 * components, import of a CycloneDX/SPDX document, export) and the business
 * service card (the software on the ports the service is called on). The
 * software map page is pages/SoftwareMap.tsx. See sbom_service.
 */

/** The API refuses with this code when the tenant has no CENSO SBOM. */
export function nonLicenziato(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'feature_not_licensed'
}

export function NotaLicenzaSbom() {
  return (
    <p className="text-muted">
      {t('Non compreso nella licenza')}
    </p>
  )
}

const KIND_LABEL: Record<string, string> = {
  java: 'Java', node: 'Node.js', python: 'Python', dotnet: '.NET', go: 'Go', php: 'PHP', ruby: 'Ruby', iis: 'IIS',
}

export function kindLabel(kind: string): string {
  if (kind === 'native') return t('Eseguibile')
  if (kind === 'imported') return t('Importata')
  if (kind === 'installed') return t('Installata')
  if (kind === 'os') return t('Software di sistema')
  if (kind === 'extra') return t('Software extra sistema')
  if (kind === 'custom') return t('Percorso personalizzato')
  return KIND_LABEL[kind] ?? kind
}

/** The name to show: the OS application is named by its distribution. */
export function nomeApplicazione(a: { name: string; kind: string }): string {
  if (a.kind === 'os') return a.name ? `${t('Software di sistema')} · ${a.name}` : t('Software di sistema')
  if (a.kind === 'extra') return t('Software extra sistema')
  return a.name
}

export async function scaricaJson(path: string, nome: string): Promise<void> {
  const blob = await downloadBlob(path)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  a.click()
  URL.revokeObjectURL(url)
}

export function nomeFile(testo: string): string {
  return testo.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'sbom'
}

/** «critica»/«alta» with the exploited flag: the most urgent advisory of a component. */
function VulnBadge({ c, aperto, onToggle }: { c: SbomComponent; aperto?: boolean; onToggle?: () => void }) {
  if (c.vulnerabilities.length === 0) return null
  const exploited = c.vulnerabilities.some((v) => v.exploited)
  const cvss = Math.max(...c.vulnerabilities.map((v) => v.cvss ?? 0))
  // The version that closes them all, computed by the Control Plane.
  const fixed = c.fixed ?? c.vulnerabilities.find((v) => v.fixed)?.fixed
  const tone = exploited || cvss >= 9 ? 'error' : cvss >= 7 ? 'warn' : 'info'
  const testo = (
    <>
      {t('{n} vulnerabilità', { n: c.vulnerabilities.length })}
      {exploited ? ` · ${t('sfruttata attivamente (CISA KEV)')}` : ''}
      {fixed ? ` · ${t('corretta in {v}', { v: fixed })}` : ''}
    </>
  )
  if (!onToggle) return <span className={`badge badge--${tone}`}>{testo}</span>
  return (
    <button type="button" className="linklike" aria-expanded={Boolean(aperto)}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle() }}
      title={aperto ? t('Nascondi il dettaglio') : t('Mostra il dettaglio')}>
      <span className={`badge badge--${tone}`}>{testo} {aperto ? '▴' : '▾'}</span>
    </button>
  )
}

/** The advisories table, shared by the CI section and the software map. */
export function TabellaAvvisi({ vulnerabilita }: { vulnerabilita: SbomVulnerability[] }) {
  const ordinate = [...vulnerabilita].sort(
    (a, b) => Number(b.exploited) - Number(a.exploited) || (b.cvss ?? 0) - (a.cvss ?? 0))
  const colonne: Column<SbomVulnerability>[] = [
    {
      key: 'id',
      header: t('Avviso'),
      render: (v) => (
        <>
          <a href={`https://osv.dev/vulnerability/${encodeURIComponent(v.id)}`} target="_blank" rel="noreferrer noopener" className="mono">{v.id}</a>
          {v.exploited && <><br /><LinkKev cve={v.cves[0]} /></>}
        </>
      ),
    },
    {
      key: 'cves',
      header: t('CVE'),
      value: (v) => v.cves.join(', '),
      render: (v) =>
        v.cves.length ? v.cves.map((cve) => (
          <div key={cve}>
            <a href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(cve)}`} target="_blank" rel="noreferrer noopener" className="mono">{cve}</a>
          </div>
        )) : '—',
    },
    { key: 'cvss', header: 'CVSS', type: 'number' },
    {
      key: 'fixed',
      header: t('Corretta in'),
      mono: true,
      render: (v) => v.fixed ?? <span className="text-muted">{t('nessuna correzione indicata')}</span>,
    },
    { key: 'summary', header: t('Descrizione'), render: (v) => <small>{v.summary || '—'}</small> },
  ]
  // Exploited first, then by score: the order the rows come in.
  return <DataTable rows={ordinate} columns={colonne} rowKey={(v) => v.id} conteggio={{ nome: t('avvisi') }} />
}

/** What the path of a component is, in words, and how a fix reaches it: the
 *  agent records the file the library was read from, which means different
 *  things per ecosystem (a JAR, a node_modules folder, a lock file, an
 *  executable that has the library compiled in). */
export function dovesiTrova(c: { ecosystem: string; path: string }): { cosa: string; file: string; dentro: string | null; come: string } | null {
  if (!c.path) return null
  const [file, ...annidato] = c.path.split('!/')
  const dentro = annidato.length > 0 ? annidato.join(' → ') : null
  const nome = (file.split(/[\\/]/).pop() || '').toLowerCase()
  switch (c.ecosystem) {
    case 'golang':
      return { cosa: t('compilata dentro l’eseguibile'), file, dentro,
        come: t('Non è un file a parte: va ricompilato il programma con la versione nuova del modulo (o installata la sua versione aggiornata).') }
    case 'maven':
      return dentro
        ? { cosa: t('JAR dentro l’archivio'), file, dentro, come: t('La libreria è impacchettata dentro l’applicazione: va ricostruito e ridistribuito l’archivio con la versione nuova.') }
        : { cosa: t('file JAR'), file, dentro, come: t('Si sostituisce il JAR con la versione nuova, o si aggiorna la dipendenza e si ridistribuisce l’applicazione.') }
    case 'npm':
      if (nome.endsWith('.json') || nome.endsWith('.lock') || nome.endsWith('.yaml'))
        return { cosa: t('elencata nel file delle dipendenze'), file, dentro, come: t('Si aggiorna la dipendenza nel progetto e si reinstalla (npm, yarn o pnpm).') }
      return { cosa: t('cartella del pacchetto in node_modules'), file, dentro, come: t('Si aggiorna la dipendenza nel progetto e si reinstalla (npm, yarn o pnpm).') }
    case 'pypi':
      return { cosa: t('pacchetto Python installato'), file, dentro, come: t('Si aggiorna con pip nell’ambiente che contiene questa cartella (virtualenv o Python di sistema).') }
    case 'nuget':
      return { cosa: nome.endsWith('.deps.json') ? t('elencata nel manifesto delle dipendenze .NET') : t('elencata nel file dei pacchetti .NET'), file, dentro,
        come: t('Si aggiorna il pacchetto NuGet nel progetto e si ripubblica l’applicazione.') }
    case 'composer':
      return { cosa: t('elencata nel file delle dipendenze PHP'), file, dentro, come: t('Si aggiorna con composer nella cartella del progetto.') }
    case 'gem':
      return { cosa: t('elencata nel file delle dipendenze Ruby'), file, dentro, come: t('Si aggiorna con bundler nella cartella del progetto.') }
    default:
      return { cosa: t('trovata in'), file, dentro, come: '' }
  }
}

/** The path of a component with what it is. */
function Percorso({ c, esteso = false }: { c: SbomComponent; esteso?: boolean }) {
  const dove = dovesiTrova(c)
  if (!dove) return null
  return (
    <>
      <small className="text-muted">{dove.cosa}: </small>
      <small className="mono" style={{ wordBreak: 'break-all' }} title={c.purl}>{dove.file}</small>
      {dove.dentro && <><small className="text-muted"> → </small><small className="mono" style={{ wordBreak: 'break-all' }}>{dove.dentro}</small></>}
      {esteso && dove.come && <><br /><small className="text-muted">{dove.come}</small></>}
    </>
  )
}

/** The advisories of a component: what, how bad, where it is fixed, where to read more. */
function DettaglioVulnerabilita({ c, copie = [c] }: { c: SbomComponent; copie?: SbomComponent[] }) {
  const conPercorso = copie.filter((x) => x.path)
  return (
    <div className="card__inset">
      <p style={{ marginTop: 0 }}>
        <strong className="mono">{c.name} {c.version}</strong>{' '}
        <small className="text-muted">({c.ecosystem}{c.purl ? ` · ${c.purl}` : ''})</small>
      </p>
      {conPercorso.length === 1 && <p style={{ margin: '4px 0 8px' }}><strong>{t('Dove si trova')}</strong><br /><Percorso c={conPercorso[0]} esteso /></p>}
      {conPercorso.length > 1 && (
        <div style={{ margin: '4px 0 8px' }}>
          <strong>{t('Dove si trova ({n} file)', { n: conPercorso.length })}</strong>
          <ul style={{ margin: '2px 0', paddingLeft: 18 }}>
            {conPercorso.map((x) => <li key={x.id}><Percorso c={x} /></li>)}
          </ul>
          {dovesiTrova(c)?.come && <small className="text-muted">{dovesiTrova(c)?.come}</small>}
        </div>
      )}
      {c.fixed && <p>{t('Per chiudere tutti gli avvisi: almeno la versione {v}.', { v: c.fixed })}</p>}
      {c.vulnerabilities.length > 0 && <TabellaAvvisi vulnerabilita={c.vulnerabilities} />}
      {c.via.length > 0 && (
        <p className="field__hint">{t('Non è dichiarata dall’applicazione: arriva tramite {catena}. Si corregge aggiornando {prima} o forzando la versione di {nome}.', { catena: c.via.join(' → '), prima: c.via[0], nome: c.name })}</p>
      )}
    </div>
  )
}

export function etichettaDipendenza(scope: string): string {
  if (scope === 'direct') return t('diretta')
  if (scope === 'transitive') return t('transitiva')
  return '—'
}

/** A library at one version, with the files it was found in. */
type GruppoComponente = { c: SbomComponent; copie: SbomComponent[] }

export function TabellaComponenti({ componenti }: { componenti: SbomComponent[] }) {
  const [soloVulnerabili, setSoloVulnerabili] = useState(false)
  const [aperti, setAperti] = useState<Set<string>>(new Set())
  const apri = (id: string) => setAperti((prima) => {
    const dopo = new Set(prima)
    if (dopo.has(id)) dopo.delete(id)
    else dopo.add(id)
    return dopo
  })
  const [dipendenza, setDipendenza] = useState('')
  const conScope = componenti.some((c) => c.scope)
  const righe = componenti.filter((c) =>
    (!soloVulnerabili || c.vulnerabilities.length > 0) && (!dipendenza || c.scope === dipendenza))
  // The same library at the same version found in several files (one Go
  // module in many executables, one JAR in many applications) is one row
  // with its files listed, not one identical row per file.
  const gruppi = (() => {
    const per = new Map<string, { c: SbomComponent; copie: SbomComponent[] }>()
    for (const c of righe) {
      const k = `${c.ecosystem}|${c.name}|${c.version}`
      const g = per.get(k)
      if (g) g.copie.push(c)
      else per.set(k, { c, copie: [c] })
    }
    return [...per.values()]
  })()
  const colonne: Column<GruppoComponente>[] = [
    {
      key: 'name',
      header: t('Componente'),
      value: ({ c }) => `${c.name}${c.via.length > 0 ? ` ${t('tramite')} ${c.via.join(' → ')}` : ''}`,
      render: ({ c, copie }) => (
        <>
          <span className="mono">{c.name}</span>
          {c.via.length > 0 && <><br /><small className="text-muted">{t('tramite')} {c.via.join(' → ')}</small></>}
          {copie.length === 1 && c.path && <><br /><Percorso c={c} /></>}
          {copie.length > 1 && (
            <><br /><button className="link" style={{ fontSize: 12 }} onClick={() => apri(c.id)}
              title={copie.map((x) => x.path).join('\n')}>
              {t('in {n} file', { n: copie.length })} {aperti.has(c.id) ? '▴' : '▾'}
            </button></>
          )}
        </>
      ),
    },
    { key: 'version', header: t('Versione'), mono: true, value: ({ c }) => c.version },
    { key: 'ecosystem', header: t('Ecosistema'), value: ({ c }) => c.ecosystem },
    ...(conScope
      ? [{
          key: 'scope',
          header: <span title={t('Diretta: dichiarata dall’applicazione. Transitiva: arriva tramite un’altra libreria.')}>{t('Dipendenza')}</span>,
          label: t('Dipendenza'),
          value: ({ c }: GruppoComponente) => etichettaDipendenza(c.scope),
        }]
      : []),
    { key: 'licenses', header: t('Licenze'), value: ({ c }) => c.licenses.join(', ') },
    {
      key: 'vulnerabilities',
      header: t('Vulnerabilità'),
      type: 'number',
      align: 'left',
      value: ({ c }) => c.vulnerabilities.length || null,
      render: ({ c }) => <VulnBadge c={c} aperto={aperti.has(c.id)} onToggle={() => apri(c.id)} />,
    },
  ]
  return (
    <>
      <div className="toolbar">
        {componenti.some((c) => c.vulnerabilities.length > 0) && (
          <label className="check">
            <input type="checkbox" checked={soloVulnerabili} onChange={(e) => setSoloVulnerabili(e.target.checked)} />{' '}
            {t('Solo i componenti vulnerabili')}
          </label>
        )}
        {conScope && (
          <select value={dipendenza} onChange={(e) => setDipendenza(e.target.value)} aria-label={t('Dipendenza')}>
            <option value="">{t('Dirette e transitive')}</option>
            <option value="direct">{t('Solo dirette')}</option>
            <option value="transitive">{t('Solo transitive')}</option>
          </select>
        )}
      </div>
      <DataTable
        rows={gruppi}
        columns={colonne}
        rowKey={(g) => g.c.id}
        conteggio={{ nome: t('componenti') }}
        expand={(g) => <DettaglioVulnerabilita c={g.c} copie={g.copie} />}
        expanded={(g) => aperti.has(g.c.id)}
      />
    </>
  )
}

/** One node of the dependency tree, its children opened on demand. A dot
 *  marks the chains towards a vulnerable library: full when the library itself
 *  is vulnerable, a ring when it only brings one in further down. */
function Nodo({ c, byPurl, antenati, catena, soloCatene }: {
  c: SbomComponent
  byPurl: Map<string, SbomComponent>
  antenati: Set<string>
  /** purl → how many vulnerable libraries are reachable from it (itself included). */
  catena: Map<string, number>
  soloCatene: boolean
}) {
  const [dettaglio, setDettaglio] = useState(false)
  const figli = c.depends_on.map((p) => byPurl.get(p)).filter((x): x is SbomComponent => Boolean(x))
    .filter((f) => !soloCatene || catena.has(f.purl))
  const vulnerabile = c.vulnerabilities.length > 0
  const sotto = (catena.get(c.purl) ?? 0) - (vulnerabile ? 1 : 0)
  const pallino = vulnerabile || sotto > 0 ? (
    <span aria-hidden="true" title={vulnerabile ? t('vulnerabile') : t('porta dentro {n} librerie vulnerabili', { n: sotto })}
      style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle',
        background: vulnerabile ? 'var(--error)' : 'transparent', border: `2px solid ${vulnerabile ? 'var(--error)' : 'var(--warn)'}` }} />
  ) : null
  const etichetta = (
    <>
      {pallino}
      <span className="mono">{c.name}</span> <span className="mono text-muted">{c.version}</span>{' '}
      <VulnBadge c={c} aperto={dettaglio} onToggle={() => setDettaglio((x) => !x)} />
      {!vulnerabile && sotto > 0 && <small style={{ color: 'var(--warn)' }}> {t('porta dentro {n} librerie vulnerabili', { n: sotto })}</small>}
      {c.path && <><br /><span style={{ marginLeft: pallino ? 15 : 0 }}><Percorso c={c} /></span></>}
      {dettaglio && <DettaglioVulnerabilita c={c} />}
    </>
  )
  // A cycle or a very deep chain stops here: the node is shown, not opened again.
  if (figli.length === 0 || antenati.has(c.purl) || antenati.size >= 12) {
    return <li>{etichetta}{antenati.has(c.purl) && <small className="text-muted"> ({t('già sopra')})</small>}</li>
  }
  const prossimi = new Set(antenati).add(c.purl)
  return (
    <li>
      <details open={soloCatene || undefined}>
        <summary>{etichetta} <small className="text-muted">({figli.length})</small></summary>
        <ul className="albero">
          {figli.map((f) => <Nodo key={f.id} c={f} byPurl={byPurl} antenati={prossimi} catena={catena} soloCatene={soloCatene} />)}
        </ul>
      </details>
    </li>
  )
}

/** For every component, how many vulnerable libraries it brings in (itself
 *  included): walking up from each vulnerable one along «required by». */
function catenaVulnerabile(componenti: SbomComponent[]): Map<string, number> {
  const chiRichiede = new Map<string, string[]>()
  for (const c of componenti) for (const d of c.depends_on) chiRichiede.set(d, [...(chiRichiede.get(d) ?? []), c.purl])
  const conta = new Map<string, number>()
  for (const v of componenti.filter((c) => c.purl && c.vulnerabilities.length > 0)) {
    const visti = new Set<string>([v.purl])
    const coda = [v.purl]
    while (coda.length) {
      const p = coda.shift()!
      conta.set(p, (conta.get(p) ?? 0) + 1)
      for (const su of chiRichiede.get(p) ?? []) if (!visti.has(su)) { visti.add(su); coda.push(su) }
    }
  }
  return conta
}

function AlberoDipendenze({ componenti }: { componenti: SbomComponent[] }) {
  const [soloCatene, setSoloCatene] = useState(false)
  const byPurl = new Map(componenti.filter((c) => c.purl).map((c) => [c.purl, c]))
  const richiesti = new Set(componenti.flatMap((c) => c.depends_on))
  const radici = componenti.filter((c) => c.scope === 'direct')
  const catena = catenaVulnerabile(componenti)
  const partenze = (radici.length > 0 ? radici : componenti.filter((c) => !richiesti.has(c.purl)))
    .filter((c) => !soloCatene || catena.has(c.purl))
  return (
    <>
      {catena.size > 0 && (
        <div className="toolbar">
          <label className="check">
            <input type="checkbox" checked={soloCatene} onChange={(e) => setSoloCatene(e.target.checked)} />{' '}
            {t('Solo le catene che portano a una libreria vulnerabile')}
          </label>
          <small className="text-muted">{t('● vulnerabile · ○ porta dentro una libreria vulnerabile')}</small>
        </div>
      )}
      <ul className="albero">
        {partenze.map((c) => <Nodo key={c.id} c={c} byPurl={byPurl} antenati={new Set()} catena={catena} soloCatene={soloCatene} />)}
      </ul>
    </>
  )
}

/** Downloads one application as its own CycloneDX document. */
export function ScaricaCycloneDxApplicazione({ app, ciName, onErrore }: {
  app: Pick<SbomApplication, 'id' | 'ci_id' | 'name' | 'kind'>
  ciName?: string | null
  onErrore: (e: unknown) => void
}) {
  const file = nomeFile([ciName, nomeApplicazione(app)].filter(Boolean).join('-'))
  return (
    <button type="button" className="linklike"
      onClick={() => scaricaJson(api.applicationSbomCycloneDxPath(app.ci_id, app.id), `${file}.cdx.json`).catch(onErrore)}>
      {t('Scarica CycloneDX')}
    </button>
  )
}

function Applicazione({ app, ciName, onDelete }: { app: SbomApplication; ciName?: string | null; onDelete?: () => void }) {
  const [albero, setAlbero] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const conAlbero = app.components.some((c) => c.depends_on.length > 0)
  return (
    <details className="card__inset" style={{ marginBottom: 8 }}>
      <summary>
        <strong>{nomeApplicazione(app)}</strong>{' '}
        <small className="text-muted">
          {kindLabel(app.kind)}
          {app.root && <> · <span className="mono">{app.root}</span></>}
          {app.ports.length > 0 && ` · ${t('porte')} ${app.ports.join(', ')}`}
          {app.process && ` · ${app.process}`}
          {` · ${t('{n} componenti', { n: app.components.length })}`}
          {(app.direct > 0 || app.transitive > 0) && ` (${t('{d} dirette, {tr} transitive', { d: app.direct, tr: app.transitive })})`}
        </small>{' '}
        {app.vulnerable > 0 && (
          <span className="badge badge--warn">{t('{n} vulnerabili', { n: app.vulnerable })}</span>
        )}
      </summary>
      <p className="field__hint">
        {app.source === 'agent' ? t('Letta dall’agente CENSO')
          : app.source === 'inventory' ? (app.kind === 'os'
            ? t('I pacchetti del sistema operativo, pubblicati dalla distribuzione (o da Microsoft su Windows), dall’inventario software del CI. Le loro vulnerabilità sono nella sezione Sicurezza del CI.')
            : t('Il software installato che non è del sistema operativo: pacchetti di terze parti anche se installati con rpm/deb, programmi di altri produttori, prodotti scompattati, percorsi personalizzati. Dall’inventario software del CI; le vulnerabilità sono nella sezione Sicurezza.'))
            : `${t('Importata')}: ${app.origin}`}
        {app.root && <> · <span className="mono">{app.root}</span></>}
        {app.updated_at && ` · ${new Date(app.updated_at).toLocaleString(localeCorrente())}`}
        {app.truncated && ` · ${t('elenco troncato (limiti di lettura)')}`}
      </p>
      {app.source === 'agent' && app.components.length > 0 && !app.components.some((c) => c.path) && (
        <p className="field__hint" style={{ color: 'var(--warn)' }}>
          {t('I percorsi dei componenti non sono arrivati: la distinta di questa macchina superava lo spazio di una consegna e l’agente li ha tolti. Con l’agente CENSO aggiornato ogni file viaggia una volta sola e i percorsi restano; al prossimo inventario compaiono.')}
        </p>
      )}
      <ErrorBanner error={errore} />
      {(conAlbero || app.components.length > 0) && <div className="toolbar" style={{ alignItems: 'center' }}>
        {conAlbero && (
          <label className="check">
            <input type="checkbox" checked={albero} onChange={(e) => setAlbero(e.target.checked)} />{' '}
            {t('Mostra come albero delle dipendenze')}
          </label>
        )}
        {app.components.length > 0 && <ScaricaCycloneDxApplicazione app={app} ciName={ciName} onErrore={setErrore} />}
      </div>}
      {app.components.length ? (
        albero ? <AlberoDipendenze componenti={app.components} /> : <TabellaComponenti componenti={app.components} />
      ) : (
        <p className="text-muted">{t('Nessun componente riconosciuto in questa applicazione.')}</p>
      )}
      {onDelete && app.source === 'import' && (
        <button className="linklike" onClick={onDelete}>{t('Elimina l’importazione')}</button>
      )}
    </details>
  )
}

/** The CI page section «Software (SBOM)»: CIDetail loads the data (and
 *  hides the section without the licence), this shows and imports. */
export function SezioneSbomCI({ ciId, ciName, puoImportare, dati, onReload }: {
  ciId: string
  ciName: string
  puoImportare: boolean
  dati: SbomView
  onReload: () => void
}) {
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)

  async function importa(file: File) {
    setBusy(true)
    setErrore(null)
    setEsito(null)
    try {
      const r = await api.importSbom(ciId, await file.text())
      setEsito(t('Importata «{a}» ({f}): {n} componenti, {v} vulnerabili.', {
        a: r.application, f: r.format, n: r.components, v: r.vulnerable }))
      onReload()
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  async function elimina(id: string) {
    if (!window.confirm(t('Eliminare questa applicazione e il suo SBOM? Non si può annullare.'))) return
    setErrore(null)
    try {
      await api.deleteSbomApplication(id)
      onReload()
    } catch (err) {
      setErrore(err)
    }
  }

  const v = dati
  return (
    <Card
      title={t('Software (SBOM)')}
      actions={v && v.applications.length > 0 ? (
        <button className="linklike" onClick={() => scaricaJson(api.ciSbomCycloneDxPath(ciId), `${nomeFile(ciName)}.cdx.json`).catch(setErrore)}>
          {t('Scarica CycloneDX di tutto il CI')}
        </button>
      ) : undefined}
    >
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Il software installato su questo CI con le librerie che contiene, letto dall’agente CENSO (cartelle di installazione, processi, servizi, siti IIS), più quello importato da documenti CycloneDX o SPDX (fornitori, pipeline di build). Porte e processo, quando ci sono, legano l’applicazione alle chiamate e al traffico. Le vulnerabilità vengono dal catalogo OSV e finiscono anche fra i rischi di sicurezza del CI.')}
      </p>
      <ErrorBanner error={errore} />
      {esito && <div className="banner banner--info">{esito}</div>}
      {v.applications.length ? (
        <>
          <p>
            {t('{a} applicazioni, {c} componenti, {v} vulnerabili.', { a: v.applications.length, c: v.components, v: v.vulnerable })}
          </p>
          {v.applications.map((a) => (
            <Applicazione key={a.id} app={a} ciName={ciName} onDelete={puoImportare ? () => void elimina(a.id) : undefined} />
          ))}
        </>
      ) : (
        <EmptyState
          title={t('Nessuna applicazione')}
          hint={t('Accendi «SBOM» nelle impostazioni dell’agente CENSO di questa macchina, oppure importa un documento CycloneDX o SPDX.')}
        />
      )}
      {puoImportare && (
        <div className="field">
          <label htmlFor={`sbom-${ciId}`}>{busy ? t('Importazione…') : t('Importa un documento CycloneDX o SPDX (JSON)')}</label>
          <input id={`sbom-${ciId}`} type="file" accept=".json,application/json" disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void importa(f) }} />
          <p className="field__hint">{t('Un documento con lo stesso nome di applicazione sostituisce quello importato prima.')}</p>
        </div>
      )}
    </Card>
  )
}

/** The business service card «Software del servizio». */
export function SoftwareServizio({ serviceId, serviceName }: { serviceId: string; serviceName: string }) {
  const dati = useAsync(() => api.serviceSbom(serviceId), [serviceId])
  const [errore, setErrore] = useState<unknown>(null)
  const [vista, setVista] = useState<'struttura' | 'runtime'>('struttura')
  if (dati.error && nonLicenziato(dati.error)) return null
  const v = dati.data
  const conSoftware = (v?.members ?? []).filter((m) => m.applications.length > 0)
  return (
    <Card
      title={t('Software del servizio (SBOM)')}
      actions={conSoftware.length > 0 ? (
        <button className="linklike" onClick={() => scaricaJson(api.serviceSbomCycloneDxPath(serviceId), `${nomeFile(serviceName)}.cdx.json`).catch(setErrore)}>
          {t('Scarica CycloneDX')}
        </button>
      ) : undefined}
    >
      <div className="toolbar">
        <button className={vista === 'struttura' ? '' : 'secondary'} onClick={() => setVista('struttura')}>{t('Struttura')}</button>
        <button className={vista === 'runtime' ? '' : 'secondary'} onClick={() => setVista('runtime')}>{t('Runtime')}</button>
      </div>
      {vista === 'runtime' ? <RuntimeServizio serviceId={serviceId} /> : <>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Per ogni componente del servizio, le applicazioni in ascolto sulle porte su cui il servizio lo chiama, con le loro librerie: la distinta del software del servizio, da allegare al registro DORA.')}
      </p>
      <ErrorBanner error={errore ?? dati.error} />
      {dati.loading && !v ? (
        <Spinner />
      ) : conSoftware.length ? (
        <>
          <p>{t('{c} componenti, {v} vulnerabili.', { c: v?.components ?? 0, v: v?.vulnerable ?? 0 })}</p>
          {conSoftware.map((m) => (
            <div key={m.ci_id} style={{ marginBottom: 12 }}>
              <h3 style={{ margin: '8px 0' }}>
                <Link to={`/cis/${m.ci_id}`}>{m.ci_name ?? m.ci_id}</Link>{' '}
                <small className="text-muted">{m.ports.length ? `${t('porte')} ${m.ports.join(', ')}` : ''}</small>
              </h3>
              {m.applications.map((a) => <Applicazione key={a.id} app={a} ciName={m.ci_name} />)}
            </div>
          ))}
        </>
      ) : (
        <p className="text-muted">
          {t('Nessuno SBOM sui componenti del servizio: accendi «SBOM» negli agenti CENSO dei server, o importa i documenti dei fornitori nella sezione «Software (SBOM)» dei CI.')}
        </p>
      )}
      </>}
    </Card>
  )
}
